import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { name: '首页', path: '/', anchor: '' },
  { name: '平台优势', path: '/', anchor: 'advantages' },
  { name: '服务类目', path: '/', anchor: 'services' },
  { name: '数字权益', path: '/', anchor: 'digital-benefits' },
  { name: '合作伙伴', path: '/', anchor: 'partners' },
  { name: '解决方案', path: '/', anchor: 'solutions' },
  { name: '关于我们', path: '/', anchor: 'about' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // 监听滚动位置，高亮对应导航项
      const anchors = ['advantages', 'services', 'digital-benefits', 'partners', 'solutions', 'about'];
      let current = '';
      for (const anchor of anchors) {
        const el = document.getElementById(anchor);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100) {
            current = anchor;
          }
        }
      }
      setActiveAnchor(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, item: typeof navItems[0]) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (!item.anchor) {
      // 首页：直接跳到顶部
      if (location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate('/');
      }
      return;
    }

    if (location.pathname === '/') {
      // 已在首页，直接滚动
      const el = document.getElementById(item.anchor);
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } else {
      // 不在首页，先跳转首页再滚动
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(item.anchor);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 400);
    }
  };

  const isActive = (item: typeof navItems[0]) => {
    if (!item.anchor) {
      return location.pathname === '/' && activeAnchor === '';
    }
    return location.pathname === '/' && activeAnchor === item.anchor;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* 左侧 Logo */}
          <a href="/" onClick={(e) => handleNavClick(e, navItems[0])} className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/5f9cc22383cc9e7cc67ed543cf57b925/e2a6563c9b2dbbd2b711ba0e34459293.png"
              alt="工福通"
              className="h-12"
            />
            <div className="hidden lg:block w-px h-8 bg-gray-300"></div>
            <div className="hidden lg:block">
              <p className="text-xs text-gray-500 tracking-wider">数字化员工福利平台</p>
            </div>
          </a>

          {/* 中间导航 */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.anchor || 'home'}
                href={item.anchor ? `#${item.anchor}` : '/'}
                onClick={(e) => handleNavClick(e, item)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-sm ${
                  isActive(item)
                    ? 'bg-blue-900 text-white'
                    : isScrolled
                    ? 'text-gray-700 hover:bg-blue-50'
                    : 'text-gray-900 hover:bg-white/50'
                }`}
              >
                {item.name}
              </a>
            ))}
          </div>

          {/* 右侧按钮 */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:4009885286"
              className="flex items-center gap-2 text-orange-500 font-bold hover:text-orange-600 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-phone-line text-lg"></i>
              <span>400-988-5286</span>
            </a>
            <Link
              to="/contact"
              className="px-5 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg shadow-orange-500/30 whitespace-nowrap text-sm"
            >
              企业开通
            </Link>
          </div>

          {/* 移动端菜单按钮 */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-900"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
          <div className="container mx-auto px-6 py-6 space-y-2">
            {navItems.map((item) => (
              <a
                key={item.anchor || 'home'}
                href={item.anchor ? `#${item.anchor}` : '/'}
                onClick={(e) => handleNavClick(e, item)}
                className={`block px-6 py-3 rounded-xl font-medium transition-all duration-300 cursor-pointer ${
                  isActive(item)
                    ? 'bg-blue-900 text-white'
                    : 'text-gray-700 hover:bg-blue-50'
                }`}
              >
                {item.name}
              </a>
            ))}
            <div className="pt-4 space-y-3">
              <a
                href="tel:4009885286"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-50 text-orange-500 rounded-xl font-bold hover:bg-orange-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>400-988-5286</span>
              </a>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 whitespace-nowrap"
              >
                企业开通
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
